<template>
  <div class="tw-flex tw-flex-col tw-h-full">
    <div
      class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-3 tw-shrink-0"
    >
      <button
        type="button"
        class="md:tw-hidden tw-text-sm tw-text-slate-500 hover:tw-text-slate-700"
        @click="$emit('cancel')"
      >
        ‹ Torna alla lista
      </button>
      <h3
        class="tw-font-semibold tw-text-slate-800 tw-ml-auto md:tw-ml-0 md:tw-mr-auto"
      >
        Nuovo ticket
      </h3>
    </div>

    <form
      class="tw-flex-1 tw-overflow-y-auto tw-pr-1 tw-space-y-3"
      @submit.prevent="submit"
    >
      <div>
        <label
          class="tw-text-xs tw-font-semibold tw-text-slate-600 tw-block tw-mb-1"
          >Categoria</label
        >
        <div class="tw-relative">
          <select
            v-model="category"
            class="tw-block tw-w-full tw-h-11 tw-box-border tw-appearance-none tw-rounded-lg tw-border tw-border-slate-300 tw-bg-white tw-pl-3 tw-pr-9 tw-py-0 tw-text-sm tw-leading-normal tw-text-slate-900 tw-outline-none focus:tw-border-fb-accent"
            required
          >
            <option value="">Scegli categoria</option>
            <option v-for="c in categories" :key="c.value" :value="c.value">
              {{ c.icon }} {{ c.label }}
            </option>
          </select>
          <span
            class="tw-pointer-events-none tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-500 tw-text-xs"
            aria-hidden="true"
            >▾</span
          >
        </div>
      </div>

      <div>
        <label
          class="tw-text-xs tw-font-semibold tw-text-slate-600 tw-block tw-mb-1"
          >Descrizione</label
        >
        <textarea
          v-model="text"
          placeholder="Descrivi il problema o l'idea…"
          class="tw-w-full tw-box-border tw-border tw-border-slate-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-h-28 tw-resize-none"
          required
        ></textarea>
      </div>

      <div>
        <label
          class="tw-text-xs tw-font-semibold tw-text-slate-600 tw-block tw-mb-1"
        >
          Allegati immagine (opzionali)
        </label>
        <input
          ref="fileInput"
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          multiple
          class="tw-w-full tw-box-border tw-border tw-border-slate-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm"
          @change="onFileChange"
        />
        <div
          v-if="attachments.length"
          class="tw-mt-2 tw-flex tw-flex-wrap tw-gap-2"
        >
          <div
            v-for="att in attachments"
            :key="att.id"
            class="tw-relative tw-w-20 tw-h-20 tw-rounded-lg tw-border tw-border-slate-300 tw-overflow-hidden tw-bg-slate-50"
          >
            <img
              :src="att.preview"
              class="tw-w-full tw-h-full tw-object-cover"
            />
            <button
              type="button"
              class="tw-absolute tw-top-0 tw-right-0 tw-bg-red-500 tw-text-white tw-rounded-bl tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center tw-text-xs"
              aria-label="Rimuovi"
              @click="removeAttachment(att.id)"
            >
              ×
            </button>
          </div>
        </div>
        <p class="tw-text-xs tw-text-slate-500 tw-mt-1">
          💡 Puoi incollare un'immagine con Ctrl/Cmd+V · max {{ maxFiles }} file
          · {{ maxFileMb }}MB l'uno
        </p>
      </div>

      <div class="tw-flex tw-justify-end tw-gap-2 tw-pt-1">
        <button
          type="button"
          class="tw-min-w-[110px] tw-border tw-border-slate-300 tw-text-slate-600 tw-rounded-lg tw-px-5 tw-py-2 hover:tw-bg-slate-50"
          @click="$emit('cancel')"
        >
          Annulla
        </button>
        <button
          type="submit"
          class="tw-min-w-[110px] tw-bg-fb-accent tw-text-white tw-rounded-lg tw-px-5 tw-py-2 hover:tw-bg-fb-accent-hover disabled:tw-opacity-60"
          :disabled="submitting"
        >
          {{ submitting ? "Invio…" : "Invia" }}
        </button>
      </div>

      <div v-if="message" class="tw-text-xs" :class="messageClass">
        {{ message }}
      </div>
    </form>
  </div>
</template>

<script>
import feedbackService, {
  FEEDBACK_CATEGORIES,
} from "../services/feedbackService";

export default {
  name: "FeedbackNewTicketForm",
  props: {
    pageUrl: { type: String, default: "" },
    maxFiles: { type: Number, default: 5 },
    maxFileMb: { type: Number, default: 5 },
  },
  emits: ["submitted", "cancel"],
  data() {
    return {
      categories: FEEDBACK_CATEGORIES,
      category: "",
      text: "",
      attachments: [],
      attachmentSeq: 0,
      submitting: false,
      message: "",
      messageClass: "",
    };
  },
  mounted() {
    document.addEventListener("paste", this.onPaste);
  },
  beforeUnmount() {
    document.removeEventListener("paste", this.onPaste);
  },
  methods: {
    onFileChange(e) {
      for (const file of e.target.files) this.addAttachment(file);
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    onPaste(e) {
      const items =
        (e.clipboardData || e.originalEvent?.clipboardData)?.items || [];
      for (const item of items) {
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) this.addAttachment(file);
        }
      }
    },
    addAttachment(file) {
      if (!file.type.startsWith("image/")) return;
      if (this.attachments.length >= this.maxFiles) {
        this.setError(`Massimo ${this.maxFiles} allegati.`);
        return;
      }
      if (file.size > this.maxFileMb * 1024 * 1024) {
        this.setError(`"${file.name}" supera ${this.maxFileMb}MB.`);
        return;
      }
      const id = this.attachmentSeq++;
      const reader = new FileReader();
      reader.onload = (ev) => {
        this.attachments.push({ id, file, preview: ev.target.result });
      };
      reader.readAsDataURL(file);
    },
    removeAttachment(id) {
      this.attachments = this.attachments.filter((a) => a.id !== id);
    },
    setError(msg) {
      this.message = "⚠ " + msg;
      this.messageClass = "tw-text-red-600";
    },
    async submit() {
      this.message = "";
      if (!this.category || !this.text.trim()) {
        this.setError("Compila categoria e descrizione.");
        return;
      }
      this.submitting = true;
      try {
        const { data } = await feedbackService.create({
          category: this.category,
          text: this.text.trim(),
          pageUrl: this.pageUrl,
          files: this.attachments.map((a) => a.file),
        });
        // reset
        this.category = "";
        this.text = "";
        this.attachments = [];
        const newId = data?.feedback?.id || null;
        this.$emit("submitted", newId);
      } catch (err) {
        this.setError(
          err?.response?.data?.message || "Errore di rete. Riprova."
        );
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
